import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import 'swiper/css';

import PropTypes from "prop-types";
import SkeletonCard from "../common/SkeletonCard";
import StoreCard from "./StoreCard";
import StoreCardStyle from "../../assets/stylesheets/store/StoreCard.module.css";

function StoreCardList({ items, loading, className, onBuy, revealDelay = 25, skeletonCount = 8, showDiscountBadge = false, layout = 'carousel', gridGap = 16, gridMinWidth = 260 }) {
  const [visibleIds, setVisibleIds] = useState(new Set());

  useEffect(() => {
    if (loading) {
      setVisibleIds(new Set());
      return;
    }
    if (!items?.length) {
      setVisibleIds(new Set());
      return;
    }
    setVisibleIds(new Set());
    items.forEach((item, index) => {
      const id = item.id ?? `${index}`;
      setTimeout(() => {
        setVisibleIds(prev => new Set(prev).add(id));
      }, index * revealDelay);
    });
  }, [items, loading, revealDelay]);

  // Loading state
  if (loading) {
    const count = items?.length || skeletonCount;
    if (layout === 'grid') {
      return (
        <div className={`${className || ''} ${StoreCardStyle['card-grid']}`} style={{ '--grid-gap': `${gridGap}px`, '--grid-min': `${gridMinWidth}px` }}>
          {Array.from({ length: count }).map((_, i) => (
            <SkeletonCard key={`skeleton-${i}`} />
          ))}
        </div>
      );
    }
    return (
      <div className={className}>
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonCard key={`skeleton-${i}`} />
        ))}
      </div>
    );
  }

  // Grid layout
  if (layout === 'grid') {
    return (
      <div className={`${className || ''} ${StoreCardStyle['card-grid']}`} style={{ '--grid-gap': `${gridGap}px`, '--grid-min': `${gridMinWidth}px` }}>
        {items.map((item, index) => (
          visibleIds.has(item.id ?? `${index}`) ? (
            <div key={item.id ?? index} className={StoreCardStyle['card-wrap']}>
              <StoreCard
                image={item.img || item.image}
                name={item.name}
                description={item.info || item.description}
                price={item.price}
                unit={item.unit}
                currency={item.currency}
                ctaLabel={item.ctaLabel || "Buy"}
                onBuy={() => onBuy?.(item)}
              />
              {showDiscountBadge && item.discount ? (
                <span aria-label={`${item.discount}% off`} className={StoreCardStyle['discount-badge']}>
                  -{item.discount}%
                </span>
              ) : null}
            </div>
          ) : (
            <SkeletonCard key={`skeleton-${item.id ?? index}`} />
          )
        ))}
      </div>
    );
  }

  // Carousel layout (existing)
  return (
    <div className={className}>
      <Swiper
        spaceBetween={12}
        slidesPerView={4}
        breakpoints={{
          0: { slidesPerView: 2 },
          480: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 }
        }}
        className={StoreCardStyle["store-swiper-carousel"]}
      >
        {items.map(item => (
          <SwiperSlide key={item.id}>
            {visibleIds.has(item.id) ? (
              <div className={StoreCardStyle["store-cursor-pointer"]}>
                <div className={StoreCardStyle['card-wrap']}>
                  <StoreCard
                    key={item.id}
                    image={item.img || item.image}
                    name={item.name}
                    description={item.info || item.description}
                    price={item.price}
                    unit={item.unit}
                    currency={item.currency}
                    ctaLabel={item.ctaLabel || "Buy"}
                    onBuy={() => onBuy?.(item)}
                  />
                  {showDiscountBadge && item.discount ? (
                    <span aria-label={`${item.discount}% off`} className={StoreCardStyle['discount-badge']}>
                      -{item.discount}%
                    </span>
                  ) : null}
                </div>
              </div>
            ) : (
              <SkeletonCard />)
            }
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

StoreCardList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  className: PropTypes.string,
  onBuy: PropTypes.func,
  revealDelay: PropTypes.number,
  skeletonCount: PropTypes.number,
  showDiscountBadge: PropTypes.bool,
  layout: PropTypes.oneOf(['carousel', 'grid']),
  gridGap: PropTypes.number,
  gridMinWidth: PropTypes.number,
};

StoreCardList.defaultProps = {
  items: [],
  loading: false,
  className: "",
  onBuy: undefined,
  revealDelay: 25,
  skeletonCount: 8,
  showDiscountBadge: false,
  layout: 'carousel',
  gridGap: 16,
  gridMinWidth: 260,
};

export default StoreCardList;